import Typography from '@mui/material/Typography';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Target, X } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { Colors, Sizes } from 'shared/components/ui/icon-button';
import { LinkedItemSearchMenu } from 'shared/components/ui/linked-item-search-menu';
import { useCloseTaskDetail } from 'shared/contexts/task-detail';
import { Paths } from 'shared/routes/paths';
import { ID } from 'shared/types/id';
import { LinkOptions } from 'shared/types/task';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import { useTaskDetailShortcut } from '../../hooks/use-task-detail-shortcut';
import { useTaskDetailSubmenu } from '../../hooks/use-task-detail-submenu';
import { useData } from '../../use-data';
import * as Styled from './meta-linked-item.style';

export type MetaLinkedItemProps = {
  value?: ID | null;
  onChange: (link: { id: ID; type: LinkOptions } | null) => void;
  isCreate?: boolean;
};

export const MetaLinkedItem: React.FC<MetaLinkedItemProps> = ({
  value,
  onChange,
  isCreate,
}) => {
  const { goals, lifeAreas } = useData();
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { menuOpen, openMenu, closeMenu } = useTaskDetailSubmenu();
  const navigate = useNavigate();
  const closeTaskDetail = useCloseTaskDetail();

  const item = useMemo(
    () =>
      goals.find(({ id }) => id === value) ??
      lifeAreas.find(({ id }) => id === value),
    [goals, lifeAreas, value],
  );

  const _onChange = (link: { id: ID; type: LinkOptions } | null) => {
    onChange(link);
    closeMenu();
  };

  const onShortcut = useCallback(
    (e: KeyboardEvent) => {
      // Don't trigger if Shift is pressed
      if (e.shiftKey) {
        return;
      }

      if (!menuOpen) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        openMenu();
      }
    },
    [menuOpen, openMenu],
  );

  useTaskDetailShortcut(['KeyG'], onShortcut, {
    ignoreInputFields: true,
    capture: true,
  });

  const onClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    closeMenu();
  };

  const navigateToItem = () => {
    if (!item) {
      return;
    }

    navigate(
      replaceUrlParams(
        'lifeAreaId' in item ? Paths.GoalDetail : Paths.LifeAreaDetail,
        { id: item.id },
      ),
    );
    closeTaskDetail();
  };

  return (
    <>
      {/* @ts-ignore */}
      <Styled.Container
        ref={buttonRef}
        onClick={item && !isCreate ? undefined : openMenu}
        $hasValue={!!item}
        as={item ? 'div' : undefined}
        $selected={menuOpen}
      >
        {item ? (
          <>
            <Styled.Content $isPlaceholder={!item}>
              <Styled.IconContainer>
                <Icon icon={Target} />
              </Styled.IconContainer>

              <Styled.LinkContainer>
                <Styled.Link
                  onClick={isCreate ? undefined : navigateToItem}
                  as={isCreate ? 'span' : undefined}
                >
                  {item.name}
                </Styled.Link>
                {!isCreate && (
                  <Styled.ChevronContainer>
                    <Icon icon={ChevronLeft} />
                  </Styled.ChevronContainer>
                )}
              </Styled.LinkContainer>
            </Styled.Content>

            <Styled.IconButton
              icon={X}
              onClick={onClear}
              color={Colors.Default}
              size={Sizes.Small}
            />
          </>
        ) : (
          <>
            <Styled.Content $isPlaceholder={!item}>
              <Styled.IconContainer>
                <Icon icon={Target} />
              </Styled.IconContainer>
              <Typography variant="body2">
                {t('dialogs.task-detail.meta.linked-item.placeholder')}
              </Typography>
            </Styled.Content>
          </>
        )}
      </Styled.Container>

      {menuOpen && (
        <LinkedItemSearchMenu
          referenceElement={buttonRef}
          goals={goals}
          lifeAreas={lifeAreas}
          value={value}
          onChange={_onChange}
          onOutside={closeMenu}
        />
      )}
    </>
  );
};
